import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Eyebrow, Reveal } from "./motion";
import api, { API_BASE } from "@/lib/api";

export default function BannerSection() {
    const [banners, setBanners] = useState(null);
    const [index, setIndex] = useState(0);

    useEffect(() => {
        api.get("/banners")
            .then((r) => {
                setBanners(r.data);
                setIndex(0);
            })
            .catch(() => setBanners([]));
    }, []);

    const next = useCallback(() => {
        setIndex((i) => (banners ? (i + 1) % banners.length : 0));
    }, [banners]);

    const prev = useCallback(() => {
        setIndex((i) =>
            banners ? (i - 1 + banners.length) % banners.length : 0
        );
    }, [banners]);

    useEffect(() => {
        if (!banners || banners.length < 2) return;
        const t = setInterval(next, 6000);
        return () => clearInterval(t);
    }, [next, banners]);

    if (!banners || banners.length === 0) return null;
    const active = banners[Math.min(index, banners.length - 1)];

    return (
        <section id="promo" data-testid="banner-section" className="relative py-20 lg:py-24">
            <div className="mx-auto max-w-7xl px-6">
                <Reveal>
                    <div className="flex items-end justify-between gap-6">
                        <div>
                            <Eyebrow>Promo Spesial</Eyebrow>
                            <h2 className="mt-5 max-w-xl font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
                                Penawaran terbaik bulan ini.
                            </h2>
                        </div>
                        {banners.length > 1 && (
                            <div className="flex gap-2">
                                <button
                                    data-testid="banner-prev"
                                    onClick={prev}
                                    aria-label="Banner sebelumnya"
                                    className="grid h-11 w-11 place-items-center rounded-full border border-border bg-white text-ink/60 transition-all duration-200 hover:border-brand hover:text-brand"
                                >
                                    <ChevronLeft size={19} />
                                </button>
                                <button
                                    data-testid="banner-next"
                                    onClick={next}
                                    aria-label="Banner berikutnya"
                                    className="grid h-11 w-11 place-items-center rounded-full border border-border bg-white text-ink/60 transition-all duration-200 hover:border-brand hover:text-brand"
                                >
                                    <ChevronRight size={19} />
                                </button>
                            </div>
                        )}
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <div
                        data-testid="banner-carousel"
                        className="overflow-hidden rounded-3xl bg-brand-mist/40 shadow-brand-lg ring-1 ring-border"
                    >
                        <div className="relative aspect-[3/1] w-full bg-white">                            <motion.div
                                key={active.id}
                                data-testid={`banner-slide-${index}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                className="absolute inset-0"
                            >
                                <a
                                    href={active.link_url || undefined}
                                    target={active.link_url ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    className={`relative block h-full w-full ${active.link_url ? "cursor-pointer" : "cursor-default"}`}
                                >
                                    <img
                                        src={`${API_BASE}/files/${active.image_path}`}
                                        alt={active.title || "Banner promo DigiKlin"}
                                        className="h-full w-full object-contain"
                                    />
                                {(active.title || active.description) && (
                                    <div className="hidden sm:block">
                                        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
                                        <div className="absolute bottom-0 left-0 p-10">
                                            {active.title && (
                                                <h3 className="font-display text-4xl font-extrabold text-white">
                                                    {active.title}
                                                </h3>
                                            )}
                                            {active.description && (
                                                <p className="mt-2 max-w-xl text-base leading-relaxed text-white/85">
                                                    {active.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </a>
                        </motion.div>
                        </div>

                        {banners.length > 1 && (
                            <div className="flex justify-center gap-1.5 bg-white py-3">
                                {banners.map((b, i) => (
                                    <button
                                        key={b.id}
                                        data-testid={`banner-dot-${i}`}
                                        onClick={() => setIndex(i)}
                                        aria-label={`Banner ${i + 1}`}
                                        className={`h-2 rounded-full transition-all duration-300 ${
                                            i === index ? "w-6 bg-brand" : "w-2 bg-ink/15 hover:bg-ink/30"
                                        }`}
                                    />
                                ))}
                            </div>
                        )}

                        {(active.title || active.description) && (
                            <div
                                data-testid="banner-caption-mobile"
                                className="bg-ink p-5 sm:hidden"
                            >
                                {active.title && (
                                    <h3 className="font-display text-lg font-extrabold text-white">
                                        {active.title}
                                    </h3>
                                )}
                                {active.description && (
                                    <p className="mt-1 text-sm leading-relaxed text-white/80">
                                        {active.description}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
